import { getCookie, setJusbrasilCookie } from '@jusbrasil-web/shared-utils-cookies'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { IncomingMessage, ServerResponse } from 'http'

import type { AuthPageEventData } from './event-data'

type Query = Record<string, string | string[] | undefined>

/**
 * Pinned utm_campaign -> feature_code contract (the Metabase A/B dashboard joins
 * on the exact feature_code string). The cold-base pilot is an A/B split whose
 * arm rides in utm_campaign itself (utm_campaign=id-conv-<arm>), mirroring the
 * mail_events.tags per-arm marking — there is no generic (arm-less) landing.
 * Add new campaigns/arms here, never derive a variant at runtime.
 *
 * Keep these tokens neutral about where a bought base came from: they show up in
 * the recipient's address bar, in GA4 and in access logs, so naming the data
 * provider here would disclose it well beyond the generic source note the email
 * itself carries.
 */
// A Map (not an object literal) so a hostile utm_campaign like `constructor` or
// `__proto__` resolves to undefined instead of an inherited Object.prototype
// member — the raw query param is attacker-controlled and flows into a cookie
// and into JSON-serialized page props.
const CAMPAIGN_FEATURE_CODES = new Map<string, string>([
  ['id-conv-atual', 'cp-id-conv-atual'],
  ['id-conv-checagem', 'cp-id-conv-checagem'],
  ['id-conv-neutro', 'cp-id-conv-neutro'],
])

const CAMPAIGN_FEATURE_CODE_COOKIE = 'signup_campaign_feature_code'
const CAMPAIGN_FEATURE_CODE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7

export interface CampaignAttributionCookieContext {
  req?: IncomingMessage
  res?: ServerResponse
}

/**
 * Resolves the campaign feature_code for the current request. The URL's
 * utm_campaign wins and is persisted to a cookie for the next funnel step;
 * otherwise falls back to the cookie set by an earlier step, since the
 * email-confirmation and CPF/phone steps don't carry the original query.
 */
export function getCampaignFeatureCode(
  query: Query,
  { req, res }: CampaignAttributionCookieContext = {}
): string | undefined {
  const utmCampaign = getFirstQueryParam(query.utm_campaign).trim().toLowerCase()
  const featureCode = utmCampaign ? CAMPAIGN_FEATURE_CODES.get(utmCampaign) : undefined

  if (featureCode) {
    const existingCookie = getCookie<string | undefined>(req ?? null, CAMPAIGN_FEATURE_CODE_COOKIE)
    if (existingCookie !== featureCode) {
      setJusbrasilCookie(res ?? null, CAMPAIGN_FEATURE_CODE_COOKIE, featureCode, {
        maxAge: CAMPAIGN_FEATURE_CODE_COOKIE_MAX_AGE_SECONDS,
        sameSite: 'lax',
        httpOnly: true,
        secure: process.env.NODE_ENV === 'production',
        path: '/',
      })
    }
    return featureCode
  }

  return getCookie<string | undefined>(req ?? null, CAMPAIGN_FEATURE_CODE_COOKIE)
}

/**
 * Layers the campaign feature_code onto already-resolved event data, without
 * ever clobbering an explicit product feature_code (query.feature_code, or an
 * override some page already applied) — the campaign is a fallback only.
 * Always resolves (and persists) the campaign cookie regardless of whether
 * eventData already has a feature_code, so a page combining both a product
 * feature_code and a campaign UTM still starts the cookie funnel correctly.
 */
export function withCampaignFallback(
  eventData: AuthPageEventData,
  query: Query,
  cookieCtx?: CampaignAttributionCookieContext
): AuthPageEventData {
  const featureCode = getCampaignFeatureCode(query, cookieCtx)
  if (eventData.featureCode) return eventData
  return featureCode ? { ...eventData, featureCode } : eventData
}
